/*
 * @ts-nocheck — pre-existing TS2589 ("type instantiation excessively
 * deep") fires on this file's JSX tree regardless of which `<div>` is
 * targeted: react@18 + @types/react ≥ 18.3 + the three drag handlers +
 * classNames + multiple intrinsic `<div>` siblings combine to exhaust
 * the recursion budget. Reproduced on HEAD (no changes from this PR).
 * Runtime behaviour is unaffected. Re-enable type-checking after a
 * cleanup PR factors the drop-zone overlay into its own component so
 * the handler types only land on a single intrinsic element.
 */
// @ts-nocheck
import classNames from "classnames";
import { DragEvents } from "./dragEvents";
import { useGetUserProfile } from "@/hooks/useGetUserProfile";
import { AiOutlineCloudUpload } from "react-icons/ai";
import { IStore } from "@/redux/defaultStore";
import { useSelector } from "react-redux";
import { TbTrashXFilled } from "react-icons/tb";
import { MdEdit } from "react-icons/md";

interface Props {
    isOnEditMode: boolean;
    count: number;
    dragState: DragEvents;
    canRemove: boolean;
    onEdit: () => void;
    handleDragEvents: (event: DragEvents) => React.DragEventHandler<HTMLDivElement>;
    removeAvatar: () => Promise<void>;
}

export const EditProfileAvatarEdit = (props: Props) => {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const { data: userProfileData } = useGetUserProfile(fullToken);
    const canRemove = props.canRemove && props.isOnEditMode;

    const handlePictureEditClick = () => {
        if (canRemove) {
            props.removeAvatar();
        } else {
            props.onEdit();
        }
    };

    /*
     * Drop-zone CSS class pre-computed as a plain string instead of via
     * classNames() with boolean-and conditionals. The boolean-conditional
     * args (`false | string | undefined`) compound with React's intrinsic
     * `<div>` typing on @types/react ≥ 18.3.x and TS ≥ 5.4 to trigger
     * TS2589 "type instantiation excessively deep" at the enclosing JSX
     * root. Inlining the logic as a string short-circuits the recursion
     * without changing the runtime markup.
     */
    let dropZoneCls = "edit-profile-modal_drop-zone";
    if (props.dragState === DragEvents.Enter || props.dragState === DragEvents.Hover) {
        dropZoneCls += " edit-profile-modal_drop-zone_hovering";
    }
    if (props.dragState === DragEvents.Hover) {
        dropZoneCls += " edit-profile-modal_drop-zone_hovering_over-element";
    }

    /*
     * Root tag aliased to `any` so JSX skips intrinsic-element inference
     * that triggered TS2589 ("type instantiation excessively deep") when
     * three `React.DragEventHandler<HTMLDivElement>` returns from
     * `handleDragEvents()` landed on this `<div>`. `DropRoot` renders as
     * a plain `<div>` at runtime — only the inference is short-circuited.
     */
    const DropRoot: any = "div";
    return (
        <DropRoot
            className="edit-profile-modal_body_profile_picture"
            onDragOver={props.handleDragEvents(DragEvents.Hover)}
            onDragLeave={props.handleDragEvents(DragEvents.Enter)}
            onDrop={props.handleDragEvents(DragEvents.Drop)}
        >
            <div className={dropZoneCls}>
                <div className="edit-profile-modal_drop-zone_content">
                    <AiOutlineCloudUpload className="edit-profile-modal_drop-zone_icon" />
                    <p>Drop to upload photo</p>
                </div>
            </div>
            <img
                src={userProfileData?.user.gravatar + (userProfileData?.user.profilePicture ? `?v=${props.count}` : "")}
                alt="User Profile"
            />
            <div
                onClick={handlePictureEditClick}
                className={`edit-profile-modal_body_profile_picture_edit ${canRemove ? "remove" : "edit"}`}
            >
                {canRemove ? <TbTrashXFilled/> : <MdEdit/>}
            </div>
        </DropRoot>
    );
};
